// Pagination maths for the paged-flip engine (TPL-04 / doc 29 §5.3).
//
// These are pure functions over the already-measured page model. They own the
// page-count, evenness and "does this page carry anything renderable" decisions
// so the engine keeps DOM work and this logic stays unit-testable.
//
// Moved out of lib/mnrq/paged-flip/engine.ts (1,939 lines) so the pagination
// behaviour can be covered without rendering.

import type { PagedFlipPage } from '@/lib/mnrq/paged-flip/types';

/** True when at least one of the values is a non-empty string. */
export function hasRenderableText(...values: Array<string | undefined>): boolean {
  return values.some((value) => typeof value === 'string' && value.trim().length > 0);
}

export function pageHasFlowItems(page: PagedFlipPage): boolean {
  return (page.categories || []).some((category) => (category.items || []).length > 0);
}

export function pageHasRenderableContent(page: PagedFlipPage): boolean {
  if (page.kind === 'blank') return false;

  if (page.kind === 'category-spotlight') {
    return (
      hasRenderableText(page.subtitle) ||
      (page.promotions || []).length > 0 ||
      pageHasFlowItems(page)
    );
  }

  if (
    page.kind === 'content' ||
    page.kind === 'featured-showcase' ||
    page.kind === 'new-arrivals'
  ) {
    return pageHasFlowItems(page);
  }

  return (
    hasRenderableText(page.title, page.subtitle, page.eyebrow, page.spotlightImage) ||
    (page.metaRows || []).some((row) => hasRenderableText(row.label, row.value)) ||
    (page.tocEntries || []).some((entry) => hasRenderableText(entry.label)) ||
    (page.promotions || []).length > 0 ||
    (page.categories || []).some((category) =>
      hasRenderableText(category.name, category.description, category.image),
    )
  );
}

export function removeEmptyPages(pages: PagedFlipPage[]): PagedFlipPage[] {
  return pages.filter((page) => {
    return pageHasRenderableContent(page);
  });
}

export function resequencePagedFlipPages(pages: PagedFlipPage[]): PagedFlipPage[] {
  return removeEmptyPages(pages).map((page, index) => ({
    ...page,
    pageNumber: index + 1,
  }));
}

export function ensureEvenPagedFlipPages(pages: PagedFlipPage[]): PagedFlipPage[] {
  if (pages.length === 0) return pages;

  const resequenced = resequencePagedFlipPages(pages);
  if (resequenced.length % 2 === 0) return resequenced;

  const lastPage = resequenced[resequenced.length - 1];
  const filler: PagedFlipPage = {
    id:
      lastPage?.kind === 'back-cover'
        ? `blank-filler-before-${lastPage.id}-${resequenced.length + 1}`
        : `blank-filler-final-${resequenced.length + 1}`,
    kind: 'blank',
    pageNumber: 0,
  };

  const normalizedPages =
    lastPage?.kind === 'back-cover'
      ? [...resequenced.slice(0, -1), filler, lastPage]
      : [...resequenced, filler];

  return normalizedPages.map((page, index) => ({
    ...page,
    pageNumber: index + 1,
  }));
}
